<template>
  <header
    v-scroll-track.window
    class="asd20-page-header"
    :class="classes"
    role="banner"
  >
    <div class="asd20-page-header__top" v-if="organizationOptions.length > 0">
      <client-only>
        <asd20-organization-picker
          :organization-options="organizationOptions"
          :organization="organization"
        ></asd20-organization-picker>
      </client-only>
      <slot name="top" />
    </div>
    <div class="asd20-page-header__main">
      <div
        class="asd20-page-header__content"
        :class="headerImageUrl ? 'new-header add-fade' : ''"
        :style="styles"
      >
        <div class="asd20-page-header__tools">
          <asd20-breadcrumb
            v-if="breadcrumbLinks.length"
            :links="breadcrumbLinks"
          ></asd20-breadcrumb>
          <asd20-button
            class="back"
            v-if="!breadcrumbLinks.length"
            :link="returnURL"
            target="_self"
            label="Back"
            icon="chevron"
            :iconAngle="-90"
            size="md"
            horizontal
            transparent
            centered
            reversed
          />
          <div class="language-and-tools">
            <Asd20LanguageTranslation
              v-if="languageCode"
              reversed
              :languageCode="languageCode"
              v-on="$listeners"
            />
            <Asd20Share></Asd20Share>
            <asd20-button
              class="print-button"
              icon="printer"
              size="sm"
              hide-label
              label="Print this page"
              onclick="window.print()"
            ></asd20-button>
          </div>
        </div>
        <div id="main" class="asd20-page-header__title-content">
          <h1 v-if="heading" v-html="heading" />
          <div
            v-if="publishDateTime || categories.length || $slots.meta"
            class="asd20-page-header__meta"
          >
            <span class="date-prefix">Published:</span>
            <asd20-formatted-date
              v-if="publishDateTime"
              :dateTime="publishDateTime"
            />
            <div
              class="modified-meta"
              v-if="modifiedDateTime && showModifiedDateTime"
            >
              <span class="date-prefix"> | Updated:</span>
              <asd20-formatted-date :dateTime="modifiedDateTime" />
            </div>
            <asd20-tag-group
              v-if="categories.length"
              :tags="categories"
              class="date-prefix"
            />
            <slot name="meta" />
          </div>
          <div v-if="lead" class="asd20-page-header__lead">
            <span v-html="lead"></span>
            <!-- <client-only>
              <a v-if="detailLink && !isDetailPage" :href="detailLink">
                {{ detailLinkLabel }}
              </a>
            </client-only> -->
          </div>
        </div>
      </div>

      <div v-if="imageUrl && !$slots.aside" class="asd20-page-header__image">
        <img :src="imageUrl" :alt="imageCaption" />
        <span v-if="imageCredits" class="image-credits">
          {{ truncatedDescription(imageCredits) }}
        </span>
      </div>
    </div>

    <aside class="asd20-page-header__aside" v-if="$slots.aside">
      <slot name="aside" />
    </aside>

    <div
      v-if="callsToAction.length > 0"
      class="asd20-page-header__call-to-action"
    >
      <!-- <asd20-button
        v-for="(cta, index) in callsToAction"
        :key="index"
        :size="['sm', 'md'].indexOf(mq) > -1 ? 'sm' : 'lg'"
        :label="cta.label"
        :icon="cta.icon"
        :link="cta.url"
        reversed
        horizontal
        centered
      /> -->
      <asd20-button
        v-if="callsToAction.length >= 1"
        :size="['sm', 'md'].indexOf(mq) > -1 ? 'sm' : 'lg'"
        :label="callsToAction[0].label"
        :icon="callsToAction[0].icon"
        :link="callsToAction[0].url"
        reversed
        horizontal
        centered
      />
      <asd20-button
        v-if="callsToAction.length >= 2"
        :size="['sm', 'md'].indexOf(mq) > -1 ? 'sm' : 'lg'"
        :label="callsToAction[1].label"
        :icon="callsToAction[1].icon"
        :link="callsToAction[1].url"
        reversed
        horizontal
        centered
      />
    </div>
    <div v-if="fullscreen" class="scroll-down-indicator"></div>
    <slot />
  </header>
</template>

<script>
import scrollTrack from '../../../directives/scroll-track'
import Asd20Button from '../../atoms/Asd20Button'
// import Asd20DistrictLogo from '../../../components/atoms/Asd20DistrictLogo'
import Asd20OrganizationPicker from '../../../components/organisms/Asd20OrganizationPicker'
import Asd20Breadcrumb from '../../../components/atoms/Asd20Breadcrumb'
import Asd20LanguageTranslation from '../../molecules/Asd20LanguageTranslation'
import Asd20Share from '../../molecules/Asd20Share'
import Asd20FormattedDate from '../../atoms/Asd20FormattedDate'
import Asd20TagGroup from '../../organisms/Asd20TagGroup'

import truncate from 'lodash-es/truncate'

export default {
  name: 'Asd20PageHeader',
  components: {
    Asd20Button,
    // Asd20DistrictLogo,
    Asd20OrganizationPicker,
    Asd20Breadcrumb,
    Asd20TagGroup,
    Asd20LanguageTranslation,
    Asd20Share,
    Asd20FormattedDate,
  },
  directives: { scrollTrack },
  props: {
    heading: { type: String, default: '' },
    lead: { type: String, default: '' },
    callsToAction: { type: Array, default: () => [] },
    imageUrl: { type: String, default: '' },
    headerImageUrl: { type: String, default: '' },
    imageCaption: { type: String, default: '' },
    imageCredits: { type: String, default: '' },
    fullscreen: { type: Boolean, default: false },
    detailLink: { type: String, default: '' },
    detailLinkLabel: { type: String, default: '' },
    organization: { type: Object, default: null },
    organizationOptions: { type: Array, default: () => [] },
    omitDetailLink: { type: Boolean, default: false },
    breadcrumbLinks: { type: Array, default: () => [] },
    publishDateTime: { type: String, default: '' },
    modifiedDateTime: { type: String, default: '' },
    categories: { type: Array, default: () => [] },
    languageCode: { type: String, default: '' },
  },
  data: () => ({
    returnURL: '',
  }),
  computed: {
    mq() {
      return this.$mq || 'sm'
    },
    classes() {
      return {
        'asd20-page-header--fullscreen': this.fullscreen,
        'asd20-page-header--has-cta': this.callsToAction
          ? this.callsToAction.length > 0
          : false,
      }
    },
    isDetailPage() {
      if (typeof window === 'undefined') return false
      return (
        window.location.pathname
          .replace(/\/$/, '')
          .split('/')
          .pop() ===
        this.detailLink
          .replace(/\/$/, '')
          .split('/')
          .pop()
      )
    },
    showModifiedDateTime() {
      const publishDateTimeString = new Date(this.publishDateTime)
      const modifiedDateTimeString = new Date(this.modifiedDateTime)
      // console.log(publishDateTimeString, modifiedDateTimeString)

      let publishDateTimePlusOne = new Date(
        publishDateTimeString.setDate(publishDateTimeString.getDate() + 1)
      )
      // console.log(publishDateTimePlusOne, modifiedDateTimeString)
      return modifiedDateTimeString > publishDateTimePlusOne ? true : false
    },
    styles() {
      return {
        '--imageBackground': `url('${this.headerImageUrl}')`,
      }
    },
  },
  mounted() {
    this.goBack()
  },
  methods: {
    goBack() {
      if (typeof window === 'undefined') return
      const currentLocation = window.location.href
      const currentURL = new URL(window.location.href)

      if (
        currentLocation.includes('schools') ||
        currentLocation.includes('8080')
      ) {
        this.returnURL = currentURL.origin
        return
      }

      if (document.referrer.includes('/auth/callback')) {
        this.returnURL = currentURL.origin
        return
      }

      if (
        document.referrer.startsWith('http') ||
        document.referrer.startsWith('/')
      ) {
        this.returnURL = document.referrer
      } else {
        this.returnURL = currentURL.origin
      }
    },
    truncatedDescription(description) {
      return truncate(description, { length: 60, separator: /,? +/ })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../../design/_variables.scss';
@import '../../../design/_mixins.scss';

.asd20-page-header {
  position: relative;
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  box-sizing: border-box;
  margin-bottom: space(1.5);
  .back {
    padding: 0;
    margin-top: space(1);
    // font-family: var(--website-typography__font-family-body);
    // line-height: 1.2;
    font-size: 0.85rem;
    // font-weight: normal;
    min-height: 45px;
    &::v-deep span {
      padding-right: space(0.5);
    }
  }

  &::after {
    content: '';
    display: block;
    position: absolute;
    top: space(2);
    bottom: space(-1.5);
    right: space(0);
    left: space(2);
    background-color: var(--website-page__alternate-background-color);
    z-index: -2;
    border-radius: var(--website-shape__radius-l);
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  &__tools {
    display: flex;
    justify-content: space-between;
    align-self: center;
    // margin-top: space(2);
    flex-direction: column;
    .asd20-breadcrumb,
    .back {
      color: var(--website-header__foreground-color);
      order: 2;
    }
    .language-and-tools {
      display: flex;
      align-items: center;
    }
    .print-button {
      padding: 0.375rem 0.375rem;
      border-radius: 5rem;
      background: rgba(255, 255, 255, 0.9);
      .asd20-button__label {
        font-size: 0.875em !important;
        padding-right: 0.5rem;
      }
      &:hover,
      &:focus {
        color: var(--website-button__foreground-color);
      }
    }
    &::v-deep .asd20-icon {
      width: 32px !important;
      height: 32px !important;
      --line-color: currentColor !important;
      --fill-color: var(--color__accent) !important;
      g {
        // --website-icon__line-color: var(--website-icon__line-color) !important;
        --line-color: currentColor !important;
      }
    }
    .asd20-language-translation {
      flex-grow: 0;
      width: max-content;
      background: rgba(255, 255, 255, 0.9);
      border-radius: 3rem;
      padding: 0 1em 0 0.5em;
      &::v-deep .asd20-icon {
        width: 32px !important;
        height: 32px !important;
        --line-color: var(--website-page__foreground-color) !important;
        --fill-color: var(--color__accent) !important;
        g {
          // --website-icon__line-color: var(--website-icon__line-color) !important;
          --line-color: var(--website-page__foreground-color) !important;
        }
      }
    }
    .asd20-language-loader {
      order: 1;
    }
  }

  &__top {
    --fill-one: rgba(255, 255, 255, 1);
    --fill-two: rgba(255, 255, 255, 0.625);
    position: relative;
    z-index: 101;
    // background: white;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    flex-direction: row;
    align-items: center;
    padding: space(0.5) space(1);
    margin-top: space(-1);
    &::v-deep .asd20-organization-picker {
      display: none;
    }

    // &::v-deep .asd20-district-logo {
    //   display: none;
    // }

    &::v-deep .notification-group--status {
      margin-top: space(0.5);
      margin-bottom: space(0.5);
      .notifications {
        background: transparent;
      }
      .asd20-notification__content {
        padding-right: 0;
      }
    }

    &::v-deep .asd20-notification-group--floating {
      margin-left: space(1);
    }
  }

  &__content {
    position: relative;
    background: var(--website-header__background-color)
      var(--website-header__background-style);
    color: var(--website-header__foreground-color);
    padding: space(1) space(1) space(1) space(1);
    z-index: 1;
    &::v-deep .asd20-breadcrumb {
      margin: space(1) 0;
    }
  }
  .new-header {
    background: var(--website-header__background-color) var(--imageBackground)
      left center / cover no-repeat;
  }
  // .add-fade {
  //   &::before {
  //     content: '';
  //     // height: auto;
  //     display: block;
  //     position: absolute;
  //     z-index: -1;
  //     top: 0;
  //     bottom: auto;
  //     left: 0;
  //     width: 100%;
  //     height: 100%;
  //     background: linear-gradient(
  //       30deg,
  //       rgba(6, 11, 31, 0.7) 10%,
  //       rgba(6, 11, 31, 0.5) 55%,
  //       transparent 70%
  //     );
  //   }
  // }

  h1 {
    position: relative;
    color: var(--website-header__title-color);
    @include asd20-font(
      1.5,
      var(--website-typography__font-family-headlines),
      1.2,
      700
    );
    margin: space(1) 0 space(0.5) 0;
  }

  &__meta {
    margin-bottom: space(0.5);
    .asd20-tag-group {
      margin-left: -0.5em;
    }
    .date-prefix {
      @include asd20-font(
        0.6,
        var(--website-typography__font-family-headlines)
      );
    }
    .modified-meta {
      display: inline-block;
    }
    .asd20-formatted-date {
      @include asd20-font(
        0.6,
        var(--website-typography__font-family-headlines)
      );
    }
  }

  &__lead {
    @include asd20-font(1, var(--website-typography__font-family-body));
    &::v-deep a {
      color: var(--website-header__foreground-color);
    }
  }

  &__call-to-action {
    margin: space(-1) space(1) space(-1) space(1);
    z-index: 1;
    display: flex;
    .asd20-button {
      box-sizing: border-box;
      flex-shrink: 1;
      // max-width: 40vw;
      min-height: space(2);
      font-size: 0.875rem !important;
      background-color: var(
        --website-header__button-background-color,
        var(--color__accent)
      );
      color: var(
        --website-header__button-foreground-color,
        var(--color__accent-t100)
      );
      // box-shadow: 2px 2px 2px rgb(50, 50, 50);
      position: relative;
      padding: space(0.5) space(1.5);
      transition: background-color 0.3s ease;
      z-index: 2;
      overflow: hidden;
      ::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0; /* Align with the button's left edge */
        width: 20px; /* Default arrow width */
        height: 100%; /* Match the button's height */
        clip-path: polygon(0 0, 100% 50%, 0 100%); /* Triangle pointing right */
        background-color: rgb(255, 255, 255, 0.2); /* Arrow color */
        transition: width 0.3s ease, background-color 0.3s ease; /* Smooth arrow growth */
        z-index: 0;
      }
      &:hover,
      &:focus {
        background-color: var(
          --website-header__button-hover-background-color,
          var(--color__accent-s10)
        );
        color: var(
          --website-header__button-hover-foreground-color,
          var(--color__accent-t100)
        );
        ::before {
          width: 25px; /* Arrow grows on hover */
          background-color: rgba(255, 255, 255, 0.3); /* Darker arrow on hover */
          z-index: 0;
        }
      }

      &:not(:last-child) {
        margin-right: space(0.5);
      }
    }
  }

  &__image {
    position: relative;
    img {
      width: 100%;
      // max-width: 100%;
      // max-height: 600px;
      border-top: 10px solid var(--color__accent);
    }
    .image-credits {
      position: absolute;
      text-align: right;
      color: #fff;
      background: rgba(50, 50, 50, 0.5);
      top: 0.625rem;
      right: 0;
      padding: space(0.25);
      font-family: var(--website-typography__font-family-headlines);
      font-size: 0.8rem !important;
    }
  }
  &--has-cta {
    .asd20-page-header__content {
      padding-bottom: space(2);
    }
  }
}
@media (min-width: 667px) {
  .asd20-page-header {
    &__image {
      .image-credits {
        top: inherit;
        bottom: 0;
      }
    }
    &__tools {
      .language-and-tools {
        & > *:not(:first-child) {
          margin-left: space(0.25);
        }
      }
    }
  }
}

@media (min-width: 1024px) {
  .asd20-page-header {
    .back {
      padding: 0;
      margin: 0;
    }
    &::after {
      top: space(6);
      bottom: space(-2);
      right: space(0);
      left: space(4);
    }
    &__main {
      justify-content: space-between;
      background: var(--website-header__background-color)
        var(--website-header__background-style);
    }
    &__content {
      width: 100%;
      flex-basis: 50%;
      flex-grow: 1;
      padding: space(0) space(2) space(1) space(3);
      min-height: 300px;
      // margin-right: space(3);
      background: transparent;
    }
    &__image {
      display: flex;
      max-width: 40vw;
      margin-top: space(-1);
      margin-bottom: space(-1);
      border-radius: var(--website-shape__radius-m);
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
      overflow: hidden;
      flex-grow: 1;
      min-width: 30vw;
      // min-width: 500px;
      // max-width: 500px;
      img {
        width: 100%;
        height: 100%;
        max-height: inherit;
        object-fit: cover;
        border-top: none;
        // opacity: 0;
      }
    }
    h1 {
      @include asd20-font(
        2.5,
        var(--website-typography__font-family-headlines),
        1.2,
        700
      );
    }
    &__title-content {
      position: relative;
      max-width: space(35);
      // .asd20-page-header__lead {
      //   margin-right: 25%;
      // }
    }

    &::v-deep .asd20-organization-picker {
      display: flex;
      margin: space(0) auto space(0) space(0);
    }

    &__top {
      padding: 0 space(3);
      margin: space(0.5) 0;
    }
    &__tools {
      flex-direction: row;
      flex-wrap: wrap;
      margin-top: space(1.5);
      .asd20-breadcrumb,
      .back {
        margin: 0 !important;
        order: 1;
      }
      .language-and-tools {
        order: 2;
      }
      .asd20-language-loader {
        order: 2;
      }
    }

    &__main {
      display: flex;
    }

    &__call-to-action {
      margin: space(-1) 60vw space(-1) space(3);
      .asd20-button {
        font-size: 1rem !important;
        flex-shrink: 0;
      }
    }
  }
}
</style>
